import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import Logo from '../components/Logo';
import ThemeToggle from '../components/ThemeToggle';
import Footer from '../components/Footer';
import { 
    Loader2, 
    UserPlus, 
    Shield, 
    Star,
    CheckCircle,
    AlertCircle
} from 'lucide-react';
import { referralAPI } from '../lib/api';
import { cn } from '../lib/utils';

const InvitePage = () => {
    const { code } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [validating, setValidating] = useState(false);
    const [referrer, setReferrer] = useState(null);
    const [error, setError] = useState(null);
    const [inviteCode, setInviteCode] = useState(null);

    useEffect(() => {
        validateReferralCode();
    }, [code]);

    const validateReferralCode = async () => {
        try {
            const response = await referralAPI.validate(code);
            if (response.data.valid) {
                setReferrer(response.data.referrer);
            } else {
                setError(response.data.message || 'Invalid referral code');
            }
        } catch (err) {
            setError('Invalid referral code');
        } finally {
            setLoading(false);
        }
    };

    const handleAcceptInvite = async () => {
        setValidating(true);
        try {
            // Create an invite code from the referral
            const response = await referralAPI.createInvite(code);
            setInviteCode(response.data.invite_code);
            
            // Wait a moment then redirect to signup with the invite code
            setTimeout(() => {
                navigate(`/signup?code=${response.data.invite_code}`);
            }, 2000);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to process invite');
            setValidating(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex flex-col">
            {/* Header */}
            <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <Logo size="small" />
                    <ThemeToggle />
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md">
                    {loading ? (
                        <div className="text-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
                            <p className="text-muted-foreground">Validating invite...</p>
                        </div>
                    ) : error && !inviteCode ? (
                        <Card className="bg-card border-border">
                            <CardContent className="pt-8 pb-6 text-center">
                                <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                                <h2 className="text-xl font-bold text-foreground mb-2">Invalid Invite</h2>
                                <p className="text-muted-foreground mb-6">{error}</p>
                                <Button onClick={() => navigate('/signup')} variant="outline">
                                    Go to Signup
                                </Button>
                            </CardContent>
                        </Card>
                    ) : inviteCode ? (
                        <Card className="bg-card border-border">
                            <CardContent className="pt-8 pb-6 text-center">
                                <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                                <h2 className="text-xl font-bold text-foreground mb-2">Invite Accepted!</h2>
                                <p className="text-muted-foreground mb-2">Your invite code:</p>
                                <div className="font-mono text-lg font-bold text-primary bg-primary/10 px-4 py-2 rounded-lg mb-4">
                                    {inviteCode}
                                </div>
                                <p className="text-sm text-muted-foreground">Redirecting to signup...</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <Card className="bg-card border-border">
                            <CardHeader className="text-center pb-4">
                                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <UserPlus className="h-8 w-8 text-primary" />
                                </div>
                                <CardTitle className="text-2xl font-bold text-foreground">
                                    You're Invited!
                                </CardTitle>
                                <CardDescription className="text-base">
                                    Join ClientCheck - Protecting contractors, one address at a time.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Referrer info */}
                                {referrer && (
                                    <div className="bg-accent/50 rounded-lg p-4 text-center">
                                        <p className="text-sm text-muted-foreground mb-1">Invited by</p>
                                        <p className="font-semibold text-foreground">{referrer.company_name}</p>
                                        <div className="flex items-center justify-center gap-2 mt-1">
                                            <span className="text-sm text-muted-foreground">{referrer.contractor_trade}</span>
                                            <span className="text-xs px-2 py-0.5 bg-primary/20 text-primary rounded-full">
                                                {referrer.rank_name}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Benefits */}
                                <div className="space-y-3">
                                    <h3 className="font-medium text-foreground text-center">What you'll get:</h3>
                                    <div className="space-y-2">
                                        {[
                                            { icon: Shield, text: "Check client risk before accepting jobs" },
                                            { icon: Star, text: "Build your contractor rank and reputation" },
                                            { icon: CheckCircle, text: "Join the San Diego beta community" }
                                        ].map((item, idx) => (
                                            <div key={idx} className="flex items-center gap-3 text-sm">
                                                <item.icon className="h-5 w-5 text-primary flex-shrink-0" />
                                                <span className="text-muted-foreground">{item.text}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* CTA */}
                                <Button 
                                    onClick={handleAcceptInvite}
                                    className="w-full h-12 text-base"
                                    disabled={validating}
                                    data-testid="accept-invite-btn"
                                >
                                    {validating ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <UserPlus className="h-4 w-4 mr-2" />
                                            Accept Invite & Sign Up
                                        </>
                                    )}
                                </Button>

                                <p className="text-xs text-center text-muted-foreground">
                                    By signing up, you agree to our Terms of Service.
                                </p>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default InvitePage;
